import CustomTable from "components/common/CustomTable";
import Dropdown from "components/common/Dropdown";
import React, { useEffect, useRef, useState } from "react";
import Source from "../../../Alerts/Tabs/Source";
import ReactECharts from "echarts-for-react";
import VendorAvatar from "components/common/VendorAvatar";
import { debounce, truncate } from "lodash";
import { useNavigate, useParams } from "react-router-dom";
import useGetSingleProductGMWValues from "hooks/useGetSingleProductGMWValues";
import useGetSalesOrders from "hooks/useGetSalesOrders";
import moment from "moment";
import { formatMoney, getCurrencyFromCurrencyCode, getSalesMultiBarChartData } from "helpers";
import useGetAllWarehouses from "hooks/useGetAllWarehouses";
import TextInput from "components/common/InputField/TextInput";
import Loader from "components/common/Loader";
import { displayProductChannelIcon } from "helpers/displayProductChannelIcon";
import useGetSalesGWM from "hooks/Dashboard/useGetSalesGWM";
import CustomCalendar from "components/common/CalendarComp.js/Calendar";
import Button from "components/common/Button";
import Skeleton from "react-loading-skeleton";
import useGetSalesForecast from "hooks/inventoryHooks/useGetSalesForecast";
import CustomTableLoader from "components/common/CustomTableLoader";
import millify from "millify";
import useGetSalesChannel from "hooks/Dashboard/useGetSalesChannel";
import lastSyncedTimeDiff from "helpers/lastSyncedTimeDiff";
import SynthTooltip from "components/common/SynthTooltip";
import MultiChannelSelect from "components/common/MultiChannelSelect";

const Sales = ({
    uom,
    lastSyncedDate,
    inventoryId,
    type,
}: {
    uom: string;
    lastSyncedDate: string;
    inventoryId?: string;
    type?: string;
}) => {
    const { productId: productIdInUrl } = useParams();
    const productId = type === "chat" ? inventoryId : productIdInUrl;
    const customRef = useRef(null);
    const [selectedPeriod, setSelectedPeriod] = useState<string>("6 months");
    const [displayPeriod, setDisplayPeriod] = useState<string>("");
    const [durationFilter, setDurationFilter] = useState<string>("This year");
    const [customFilter, setCustomFilter] = useState<string>("");
    const [startAndEndDate, setStartAndEndDate] = useState<string[]>([]);
    const [chartFilter, setChartFilter] = useState<string>("yearly");
    const [showCustomDate, setShowCustomDate] = useState<boolean>(false);
    const [customValue, customOnChange] = useState("");
    const [gmwValuesFilter, setGmwValuesFilter] = useState({
        location: "",
        period: "yearly",
        inventoryId: "",
    });
    const [refresh, setRefresh] = useState(false);
    const [channelFilter, setChannelFilter] = useState("");

    const {
        data,
        isFetching: gwmLoading,
        handleDateRangeChange,
        channel: salesChannelFilterValue,
        handleChannelChange,
        handleCategoryChange,
        handleStartAndEndDateChange,
        handleWarehouseChange,
    } = useGetSalesGWM(productId, true, refresh, setRefresh);

    const {
        warehouseValue: warehouseDataValue,
        searchTwo,
        handleDebouncedChangeTwo,
        handleSearchTwo,
        searchResult,
        handleFilterChange: filterChange,
        handleClearFilter: clearFilter,
    } = useGetAllWarehouses();

    const {
        salesForecast,
        fetchingSalesForecast,
        setDates,
        setWarehouseId,
        handleChannelChange: handleSalesForecastChannelChange,
    } = useGetSalesForecast(productId);

    const { data: salesChannel, isFetching: salesChannelLoading } = useGetSalesChannel();

    const salesChannelOption = (salesChannel as string[])
        ?.filter(
            (channel) =>
                channel?.toLowerCase() === "shopify" ||
                channel?.toLowerCase() === "retail" ||
                channel?.toLowerCase() === "amazon"
        )
        ?.map((channel) => {
            return {
                label: channel,
                value: channel,
                icon:
                    channel?.toLowerCase() === "shopify" ? (
                        <img
                            src="https://storage.googleapis.com/heysynth_files/synth_webapp/shopify_w87tn3.svg"
                            alt="shopify"
                            className="w-4 h-4"
                        />
                    ) : channel?.toLowerCase() === "amazon" ? (
                        <img
                            src="https://storage.googleapis.com/heysynth_files/synth_webapp/amazon_1_aeftdf.svg"
                            alt="amazon"
                            className="w-4 h-4"
                        />
                    ) : (
                        <i className="ri-store-2-fill text-y-10 text-base"></i>
                    ),
            };
        });

    useEffect(() => {
        const period = Number(selectedPeriod?.split(" ")[0]);
        const startDate = moment().startOf("month").format("MMM YYYY");
        const endDate = moment()
            .startOf("month")
            .add(period, "months")
            .subtract(1, "days")
            .format("MMM YYYY");
        const sameYear = startDate?.split(" ")[1] === endDate?.split(" ")[1];
        const sameMonth = startDate?.split(" ")[0] === endDate?.split(" ")[0];

        const text = sameMonth
            ? `(${endDate})`
            : sameYear
            ? "(" + startDate?.split(" ")[0] + "-" + endDate + ")"
            : "(" + startDate + "-" + endDate + ")";

        setDisplayPeriod(`${selectedPeriod} ${text}`);
        setDates({
            startDate: moment()?.format("YYYY-MM-DD"),
            endDate: moment(endDate, "MMM YYYY")?.endOf("month")?.format("YYYY-MM-DD"),
        });
        setGmwValuesFilter((prev) => {
            return {
                ...prev,
                period: "custom",
            };
        });
        setStartAndEndDate([
            moment()?.format("MMM DD YYYY"),
            moment(endDate, "MMM YYYY")?.endOf("month")?.format("MMM DD YYYY"),
        ]);
        handleStartAndEndDateChange([moment(), moment(endDate, "MMM YYYY")?.endOf("month")]);
    }, [selectedPeriod]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (customRef.current && !customRef.current.contains(event.target)) {
                setShowCustomDate(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [customRef]);

    const {
        data: allWarehouses,
        warehouseValue,
        search,
        handleDebouncedChange,
        handleSearch,
        handleFilterChange,
        handleClearFilter,
    } = useGetAllWarehouses();

    const {
        data: salesOrderData,
        isFetching,
        isLoadingMore,
        pagination,
        setUpdatedPage,
        handleChannelChange: handleSalesOrderChannelChange,
        channel: salesOrderChannelFilterValue,
    } = useGetSalesOrders(productId, warehouseValue?.value);

    const multiBarChartOption = {
        legend: { show: false },
        tooltip: {
            formatter: function (params) {
                return `
                <div>
                    <p style="margin-bottom: 10px">${params?.seriesName}</p>
                    <div style="display:flex ; align-items: center; gap: 10px;">
                        <div style="display:flex ; align-items: center; gap: 5px;">
                            <span style="width: 10px; height: 10px; background-color:${
                                params?.color
                            }; border-radius: 100%; "></span>
                            <span>${params?.value?.[0]}</span>
                        </div> 

                        <span style="font-weight: 600;">${
                            params?.value?.[1] || params?.value?.[2]
                        }</span>
                    </div>
                </div>
                `;
            },
        },
        color: ["#6562E5", "#56B648"],
        dataset: {
            source: [
                ["product", "Sales demand", "Sales forecasted"],
                ...getSalesMultiBarChartData(data, gmwValuesFilter?.period, startAndEndDate),
            ],
        },
        xAxis: { type: "category" },
        yAxis: {
            axisLabel: {
                formatter: "{value}",
            },
        },
        // Declare several bar series, each will be mapped
        // to a column of dataset.source by default.
        series: [
            { type: "bar", barWidth: "13%" },
            { type: "bar", barWidth: "13%" },
        ],
    };

    const tableHeader = [
        // { title: "Product name", widthClass: "w-[15.6%]" },
        { title: "Customer Name", widthClass: "w-[11.4%]" },
        { title: "Date", widthClass: "w-[9.1%]" },
        // { title: "Customer Name", widthClass: "w-[11.4%]" },
        { title: "Quantity", widthClass: "w-[7.6%]" },
        { title: "Unit Price", widthClass: "w-[7.6%]" },
        { title: "Total Price", widthClass: "w-[7.6%]" },
        { title: "Connected source", widthClass: "w-[14.6%]" },
        { title: "Order Status", widthClass: "w-[10.7%]" },
        { title: "Outlet", widthClass: "w-[15.6%]" },
    ];

    const tableBody = salesOrderData?.map((salesData, idx) => [
        // {
        //     content: (
        //         <div
        //             className={`py-3 pr-3 flex font-rocGroteskMedium items-center h-full border-slate-100 text-sm text-slate-800 `}
        //         >
        //             <div className="flex items-center space-x-3">
        //                 <VendorAvatar imageSrc={""} size={32} name={"Intercom"} />
        //                 <div>
        //                     <span className="block text-sm font-rocGroteskMedium">
        //                         SO-001 dummy
        //                     </span>
        //                     <span className="block text-sm font-rocGroteskMedium text-slate-500">
        //                         {truncate(salesData?.products?.inventory?.productName, {
        //                             length: 18,
        //                         })}
        //                     </span>
        //                 </div>
        //             </div>
        //         </div>
        //     ),
        //     // cellClickAction: () => {
        //     //     navigate(`/dashboard/inventory/products/${salesData?.products?.inventory?._id}`);
        //     // },
        // },
        {
            content: (
                <div
                    className={`py-3 pr-3 font-rocGroteskMedium border-slate-100 text-sm ${
                        !salesData?.customerDetails?.first_name &&
                        !salesData?.customerDetails?.last_name
                            ? ` text-[#94A3B8]`
                            : ` text-slate-800`
                    } `}
                >
                    <span>
                        {!salesData?.customerDetails?.first_name &&
                        !salesData?.customerDetails?.last_name &&
                        !salesData?.customerDetails?.name
                            ? `N/A`
                            : salesData?.customerDetails?.name ||
                              `${salesData?.customerDetails?.first_name || ""} ${
                                  salesData?.customerDetails?.last_name || ""
                              }`}
                    </span>
                </div>
            ),
        },
        {
            content: (
                <div
                    className={`py-3 pr-3 font-rocGroteskMedium border-slate-100 text-sm text-slate-800 `}
                >
                    <span>{moment(salesData?.saleDate)?.format("DD MMM, YYYY")}</span>
                </div>
            ),
        },
        // {
        //     content: (
        //         <div
        //             className={`py-3 pr-3 font-rocGroteskMedium border-slate-100 text-sm text-slate-800 `}
        //         >
        //             <span>
        //                 {salesData?.customerDetails?.first_name}{" "}
        //                 {salesData?.customerDetails?.last_name}
        //             </span>
        //         </div>
        //     ),
        // },
        {
            content: (
                <div
                    className={`py-3 pr-3 font-rocGroteskMedium border-slate-100 text-sm text-slate-800 `}
                >
                    <span>{salesData?.products?.quantity}</span>
                </div>
            ),
        },
        {
            content: (
                <div
                    className={`py-3 pr-3 font-rocGroteskMedium border-slate-100 text-sm text-slate-800 `}
                >
                    <span>
                        {getCurrencyFromCurrencyCode(salesData?.products?.price?.currency)}{" "}
                        {formatMoney().format(salesData?.products?.price?.amount)}
                    </span>
                </div>
            ),
        },
        {
            content: (
                <div
                    className={`py-3 pr-3 font-rocGroteskMedium border-slate-100 text-sm text-slate-800 `}
                >
                    <span>
                        {getCurrencyFromCurrencyCode(salesData?.products?.subtotal?.currency)}{" "}
                        {formatMoney().format(salesData?.products?.subtotal?.amount)}
                    </span>
                </div>
            ),
        },
        {
            content: (
                <div
                    className={`py-3 pr-3 font-rocGroteskMedium border-slate-100 text-sm text-slate-800 `}
                >
                    {salesData?.channel?.toLowerCase()?.includes("synth") ? (
                        <span>Retail </span>
                    ) : (
                        <div className="flex items-center space-x-2">
                            {displayProductChannelIcon(salesData?.channel)}
                            <span>{truncate(salesData?.channel, { length: 10 })} </span>
                        </div>
                    )}
                </div>
            ),
        },
        {
            content: (
                <div
                    className={`py-3 pr-3 font-rocGroteskMedium border-slate-100 text-sm text-slate-800 `}
                >
                    <div className="text-sm font-rocGroteskMedium flex items-center space-x-2">
                        {/* <span className="w-2 h-2 rounded-full bg-[#F59E0B]"></span> */}
                        <span className="capitalize">{salesData?.status || "Fulfilled"}</span>
                    </div>
                </div>
            ),
        },
        {
            content: (
                <div
                    className={`py-3 pr-3 font-rocGroteskMedium border-slate-100 text-sm text-slate-800 `}
                >
                    <span>{salesData?.warehouse?.warehouseName || "N/A"}</span>
                </div>
            ),
        },
    ]);

    const tableComponentHeader = [
        {
            title: "Sales forecasted",
            widthClass:
                "!border !border-slate-200 w-[30%] max-sm:w-[200px] text-[12px] rounded-l-[4px] bg-slate-50",
            tooltipIcon: "sales-forecast",
            tooltipContent: "This is the forecasted sales quantity synth is predicting.",
        },
        {
            title: "Inventory forecast",
            widthClass: "!border !border-slate-200 w-[25%] max-sm:w-[200px] bg-slate-50",
            tooltipIcon: "inventory-forecast",
            tooltipContent:
                "A projection of future inventory requirements based on analysis of past sales data, demand trends, lead times, and safety stock to ensure optimal stock levels and avoid stockouts.",
        },
        {
            title: "Incoming Inventory",
            widthClass: "!border !border-slate-200 w-[25%] max-sm:w-[200px] bg-slate-50",
            tooltipIcon: "outgoing-pos",
            tooltipContent:
                "Purchase orders that have been issued to suppliers but not yet received, representing inventory expected to arrive and replenish stock.",
        },
        {
            title: "Month",
            widthClass:
                "!border !border-slate-200 w-[20%] max-sm:w-[200px] bg-slate-50 rounded-r-[4px]",
            tooltipIcon: "month",
            tooltipContent:
                "The specific calendar month used as a time frame for analyzing or forecasting inventory levels, sales, or purchase orders.",
        },
    ];

    const tableComponentBody = salesForecast?.map((comp, idx) => {
        return [
            {
                content: (
                    <div
                        className={`py-1.5 px-3 flex font-rocGroteskMedium items-center h-full text-sm `}
                    >
                        <p className="text-[#042821]">
                            {millify(comp?.forecast?.forecast || 0, {
                                precision: 2,
                            })}
                            {` `}
                            {uom || ""}
                        </p>
                    </div>
                ),

                tableDataDivClass: "!pl-0",
                widthClass: "!border-slate-200 !border",
            },

            {
                content: (
                    <div className={`py-1.5 pr-3 font-rocGroteskMedium text-sm `}>
                        <div className="flex items-center gap-1">
                            {comp?.forecast?.inventoryForecast <= 0 && (
                                <span className="material-symbols-outlined text-[14px] text-r-50">
                                    warning
                                </span>
                            )}
                            <p
                                className={` ${
                                    comp?.forecast?.inventoryForecast <= 0
                                        ? "text-[#FF3D1D]"
                                        : "text-[#042821]"
                                }`}
                            >
                                {millify(comp?.forecast?.inventoryForecast || 0, {
                                    precision: 2,
                                })}
                                {` `}
                                {uom || ""}
                            </p>
                        </div>
                    </div>
                ),
                widthClass: "!border-slate-200",
            },
            {
                content: (
                    <div className={`py-1.5 pr-3 font-rocGroteskMedium border-slate-200 text-sm `}>
                        <p className="text-[#042821]">
                            {millify(comp?.forecast?.orderToArrive || 0, {
                                precision: 2,
                            })}

                            {` `}
                            {uom || ""}
                        </p>
                    </div>
                ),
                widthClass: "!border-slate-200",
            },
            {
                content: (
                    <div className={`py-1.5 pr-3 font-rocGroteskMedium border-slate-200 text-sm `}>
                        <p className="text-[#042821]">
                            {moment(comp?.range?.start)?.format("MMMM") === moment()?.format("MMMM")
                                ? moment(comp?.range?.start)?.format("MMMM DD, YYYY")
                                : moment(comp?.range?.start)?.format("MMMM YYYY")}
                        </p>
                    </div>
                ),
                widthClass: "!border-slate-200 !border",
            },
        ];
    });

    let totalForecast = 0;
    let totalInventoryForecast = 0;
    let totalOrderToArrive = 0;

    if (salesForecast && salesForecast.length > 0) {
        salesForecast.forEach((comp) => {
            totalForecast += Number(comp?.forecast?.forecast) || 0;
            totalInventoryForecast += Number(comp?.forecast?.inventoryForecast) || 0;
            totalOrderToArrive += Number(comp?.forecast?.orderToArrive) || 0;
        });
    }

    const newRowData = [
        {
            content: (
                <div
                    className={`py-1.5 px-3 flex font-rocGroteskMedium items-center h-full text-sm `}
                >
                    <p className="text-[#042821] font-bold">
                        <p className="text-[#042821] font-bold">
                            {millify(totalForecast || 0, {
                                precision: 2,
                            })}
                            {` `}
                            {uom || ""}
                            {` `}
                            (next {selectedPeriod})
                        </p>
                    </p>
                </div>
            ),
            tableDataDivClass: "!pl-0",
            widthClass: "!border-slate-200 !border",
        },
        {
            content: (
                // <div className={`py-1.5 pr-3 font-rocGroteskMedium text-sm `}>
                //     <p className="text-[#042821] font-bold">
                //         {millify(totalInventoryForecast || 0, {
                //             precision: 2,
                //         })}
                //         {` `}
                //         {uom || ""}
                //         {` `}
                //         (next {selectedPeriod})
                //     </p>
                // </div>
                <></>
            ),
            // widthClass: "!border-slate-200",
            widthClass: "!border-none",
        },
        {
            content: (
                // <div className={`py-1.5 pr-3 font-rocGroteskMedium border-slate-200 text-sm `}>
                //     <p className="text-[#042821] font-bold">
                //         {millify(totalOrderToArrive || 0, {
                //             precision: 2,
                //         })}
                //         {` `}
                //         {uom || ""}
                //         {` `}
                //         (next {selectedPeriod})
                //     </p>
                // </div>
                <></>
            ),
            // widthClass: "!border-slate-200",
            widthClass: "!border-none",
        },
        {
            content: <></>,
            widthClass: "!border-none",
        },
    ];

    tableComponentBody.push(newRowData);

    // const handleDateChange = (e) => {
    //     const { value } = e.target;
    //     const [val, period] = value.split("-");

    //     setDurationFilter(period);
    //     if (val !== "custom") {
    //         handleDateRangeChange(val);
    //         setStartAndEndDate([]);
    //         setChartFilter(val);
    //         customOnChange("");
    //         setShowCustomDate(false);
    //     } else {
    //         setShowCustomDate(true);
    //     }
    // };

    // useEffect(() => {
    //     if (
    //         gmwValuesFilter?.period === "custom" &&
    //         customValue[0] &&
    //         customValue[1] &&
    //         customValue[0] !== customValue[1]
    //     ) {
    //         const dates = [
    //             moment(customValue?.[0]).format("MMM DD YYYY"),
    //             moment(customValue?.[1]).format("MMM DD YYYY"),
    //         ];

    //         setStartAndEndDate([dates?.[0], dates?.[1]]);
    //         if (customValue?.[0] !== customValue?.[1]) {
    //             handleStartAndEndDateChange(customValue);
    //         }
    //     }
    // }, [gmwValuesFilter?.period, customValue]);

    const lastSyncedForcast = lastSyncedTimeDiff(lastSyncedDate);
    return (
        <div id="salesContainer" className="relative">
            {/* <Source /> */}

            <div className="py-5 relative bg-white rounded-[6px] shadow-faintShadow border border-[#E2E8F080] mb-8">
                <div
                    className={`${
                        type === "chat"
                            ? "flex flex-col space-y-4"
                            : "flex items-center justify-between"
                    } pb-5 px-6 border-b border-slate-200 mb-6`}
                >
                    <div className="flex items-center space-x-3">
                        {gwmLoading ? (
                            <div>
                                <Skeleton
                                    height={10}
                                    width={50}
                                    highlightColor="#E2E8F0"
                                    borderRadius={16}
                                    baseColor="#F1F5F9"
                                    className="custom-skeleton"
                                />

                                <Skeleton
                                    height={15}
                                    width={100}
                                    highlightColor="#E2E8F0"
                                    borderRadius={16}
                                    baseColor="#F1F5F9"
                                    className="custom-skeleton"
                                />
                            </div>
                        ) : (
                            <div>
                                <span className="block font-rocGroteskMedium text-sm text-g-75">
                                    Total sales
                                </span>
                                <span className="block font-rocGroteskMedium text-xl text-p-50">
                                    {formatMoney()?.format((data?.totalQty || 0)?.toFixed(2)) || 0}
                                </span>
                            </div>
                        )}

                        {gwmLoading ? (
                            <div>
                                <Skeleton
                                    height={10}
                                    width={50}
                                    highlightColor="#E2E8F0"
                                    borderRadius={16}
                                    baseColor="#F1F5F9"
                                    className="custom-skeleton"
                                />

                                <Skeleton
                                    height={15}
                                    width={100}
                                    highlightColor="#E2E8F0"
                                    borderRadius={16}
                                    baseColor="#F1F5F9"
                                    className="custom-skeleton"
                                />
                            </div>
                        ) : (
                            <div>
                                <span className="block font-rocGroteskMedium text-sm text-g-75">
                                    Total forecasted sales
                                </span>
                                <span className="block font-rocGroteskMedium text-xl text-[#56B648]">
                                    {formatMoney()?.format(
                                        (data?.totalForecastQty || 0)?.toFixed(2)
                                    ) || 0}
                                </span>
                            </div>
                        )}
                    </div>

                    <div
                        className={`${
                            type === "chat"
                                ? "flex flex-col space-y-3"
                                : "flex items-center space-x-4"
                        }`}
                    >
                        {gwmLoading ? (
                            <div>
                                <Skeleton
                                    height={10}
                                    width={150}
                                    highlightColor="#E2E8F0"
                                    borderRadius={16}
                                    baseColor="#F1F5F9"
                                    className="custom-skeleton"
                                />
                            </div>
                        ) : (
                            <div
                                className={`${
                                    type === "chat"
                                        ? "flex flex-col space-y-2"
                                        : "flex items-center space-x-4"
                                }`}
                            >
                                <div className="flex items-center whitespace-nowrap h-[40px] py-2 px-4 space-x-2">
                                    <span className="w-[14px] h-[14px] rounded-[2px] bg-[#56B648]"></span>
                                    <span className={`text-g-75 font-rocGroteskMedium text-sm`}>
                                        Sales forecasted
                                    </span>
                                </div>

                                {gwmLoading ? (
                                    <div>
                                        <Skeleton
                                            height={10}
                                            width={150}
                                            highlightColor="#E2E8F0"
                                            borderRadius={16}
                                            baseColor="#F1F5F9"
                                            className="custom-skeleton"
                                        />
                                    </div>
                                ) : (
                                    <div className="flex items-center whitespace-nowrap h-[40px] py-2 px-4 space-x-2">
                                        <span className="w-[14px] h-[14px] rounded-[2px] bg-p-50"></span>
                                        <span className={`text-g-75 font-rocGroteskMedium text-sm`}>
                                            Sales demand
                                        </span>
                                    </div>
                                )}
                            </div>
                        )}
                        <div className="flex items-center rounded-[5px] border border-slate-200 ">
                            <div className="border-r border-slate-200">
                                <MultiChannelSelect
                                    salesChannel={salesChannel}
                                    value={salesChannelFilterValue}
                                    onChange={(value) => {
                                        handleChannelChange(value);
                                        handleSalesForecastChannelChange(value);
                                    }}
                                    isLoading={salesChannelLoading}
                                    dropdown={
                                        <div className="flex items-center justify-between whitespace-nowrap h-[40px]  py-2 pl-3 pr-2 space-x-1 rounded-[5px]">
                                            <div className="flex items-center space-x-2">
                                                <span className="text-[13px] whitespace-nowrap font-rocGroteskMedium capitalize">
                                                    {truncate(salesChannelFilterValue, {
                                                        length: 15,
                                                    }) || "All channels"}
                                                </span>
                                                {!!salesChannelFilterValue && (
                                                    <i
                                                        onClick={(e) => {
                                                            e?.stopPropagation();
                                                            handleChannelChange("");
                                                        }}
                                                        className="ri-close-circle-fill text-sm text-slate-500"
                                                    ></i>
                                                )}
                                            </div>
                                        </div>
                                    }
                                />
                            </div>
                            <div className="border-r border-slate-200">
                                <Dropdown
                                    handleChange={(name, value) => {}}
                                    dropdown={
                                        <div className="flex items-center  whitespace-nowrap h-[40px] py-2 px-4 space-x-1 ">
                                            <i className="ri-map-pin-line text-sm text-g-75 "></i>
                                            <span
                                                className={`text-g-75 font-rocGroteskMedium text-[13px]`}
                                            >
                                                {warehouseDataValue?.name || "Warehouses"}
                                            </span>
                                            {warehouseDataValue?.name && (
                                                <i
                                                    onClick={() => {
                                                        clearFilter();
                                                        handleWarehouseChange("");
                                                        setWarehouseId("");
                                                    }}
                                                    className="ri-close-circle-line text-slate-500"
                                                ></i>
                                            )}
                                            <i className="ri-arrow-down-s-line text-g-75"></i>
                                        </div>
                                    }
                                    search={
                                        <div>
                                            <TextInput
                                                value={searchTwo}
                                                name={"search"}
                                                type={"text"}
                                                required={false}
                                                onChange={handleSearchTwo}
                                                onInput={handleDebouncedChangeTwo}
                                                inputPlaceholder="Search warehouse"
                                                containerClassname=""
                                                // inputContainerClassname={"!border-none !rounded-none "}
                                                // inputClassName="w-[50%]"
                                            />
                                        </div>
                                    }
                                    dropdownContainerClasses={`shadow-cardShadow-4  border border-slate-100  rounded `}
                                    dropdownOptions={[
                                        {
                                            customChild: (
                                                <div>
                                                    <div className="max-h-[280px]">
                                                        {searchResult?.map((warehouse) => {
                                                            return (
                                                                <div
                                                                    key={warehouse?._id}
                                                                    onClick={() => {
                                                                        filterChange(
                                                                            warehouse?.warehouseName,
                                                                            warehouse?._id
                                                                        );
                                                                        handleWarehouseChange(
                                                                            warehouse?._id
                                                                        );
                                                                        setWarehouseId(
                                                                            warehouse?._id
                                                                        );
                                                                    }}
                                                                >
                                                                    <div
                                                                        className={`flex items-center space-x-3 max-w-[300px] px-3 py-2 hover:bg-slate-100 cursor-pointer ${
                                                                            warehouseDataValue?.value ===
                                                                                warehouse?._id &&
                                                                            "bg-slate-100"
                                                                        }`}
                                                                    >
                                                                        <i className="ri-map-pin-line text-base text-g-75 "></i>
                                                                        <div>
                                                                            <span
                                                                                className={`block text-g-75 text-sm font-rocGroteskMedium ${
                                                                                    warehouse
                                                                                        ?.address
                                                                                        ?.address &&
                                                                                    "mb-[2px]"
                                                                                }`}
                                                                            >
                                                                                {truncate(
                                                                                    warehouse?.warehouseName,
                                                                                    { length: 40 }
                                                                                )}
                                                                            </span>
                                                                            <span className="block text-g-75 text-sm font-rocGroteskMedium ">
                                                                                {truncate(
                                                                                    warehouse
                                                                                        ?.address
                                                                                        ?.address,
                                                                                    { length: 40 }
                                                                                )}
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            ),
                                        },
                                    ]}
                                />
                            </div>

                            <Dropdown
                                handleChange={(name, value) => {
                                    setSelectedPeriod(value);
                                }}
                                dropdown={
                                    <div className="flex items-center justify-between whitespace-nowrap h-[40px]  py-2 px-4 space-x-1">
                                        <span
                                            className={`text-g-75 font-rocGroteskMedium text-[13px]`}
                                        >
                                            {displayPeriod}
                                        </span>
                                        <i className="ri-arrow-down-s-line text-g-75"></i>
                                    </div>
                                }
                                dropdownContainerClasses={`shadow-cardShadow-4 !w-full !max-w-full min-w-full border border-slate-100 rounded !max-h-fit !pt-0`}
                                dropdownOptions={[
                                    {
                                        label: "1 month",
                                        value: "1 month",
                                    },
                                    {
                                        label: "2 months",
                                        value: "2 months",
                                    },
                                    {
                                        label: "3 months",
                                        value: "3 months",
                                    },
                                    {
                                        label: "4 months",
                                        value: "4 months",
                                    },
                                    {
                                        label: "5 months",
                                        value: "5 months",
                                    },
                                    {
                                        label: "6 months",
                                        value: "6 months",
                                    },
                                ]}
                            />

                            {/* <div>
                                <Dropdown
                                    handleChange={(name, value) => {
                                        handleDateChange({ target: { name, value } });
                                        setGmwValuesFilter((prev) => {
                                            return {
                                                ...prev,
                                                period: value?.split("-")[0],
                                            };
                                        });
                                    }}
                                    dropdown={
                                        <div className="flex items-center whitespace-nowrap h-[40px] py-2 px-4 space-x-1 bg-slate-50 rounded-r-[5px]">
                                            <span
                                                className={`text-g-75 font-rocGroteskMedium text-[13px] capitalize`}
                                            >
                                                {startAndEndDate?.length !== 0
                                                    ? ` ${startAndEndDate[0]} to ${startAndEndDate[1]}`
                                                    : durationFilter || "This year"}
                                            </span>
                                            <i className="ri-arrow-down-s-line text-g-75"></i>
                                        </div>
                                    }
                                    dropdownItemsClasses="!bg-transparent border-b-[1px] border-b-slate-100"
                                    dropdownClassName="!w-full !mb-10 !-mt-7"
                                    dropdownContainerClasses={`shadow-cardShadow-4 border border-slate-100 right-0 rounded-[6px] !max-h-fit !w-auto`}
                                    dropdownOptions={[
                                        ...dateValues?.map((item, idx) => ({
                                            label: (
                                                <div
                                                    key={idx}
                                                    className={`${
                                                        item.period === "custom" && "capitalize"
                                                    } text-g-75 font-rocGroteskMedium text-[13px] flex justify-between
                                                    `}
                                                >
                                                    {item?.period}
                                                </div>
                                            ),
                                            value: item?.value + "-" + item?.period,
                                        })),
                                    ]}
                                />
                            </div>
                            <div className="relative">
                                {showCustomDate && (
                                    <div
                                        ref={customRef}
                                        className="w-[630px]  z-[10000] absolute  right-0 top-16 px-[28px] pt-8 pb-8 bg-white
                                                    rounded-2xl shadow-[0px_0px_4px_rgba(0,0,0,0.04),0px_4px_32px_rgba(0,0,0,0.06)]"
                                    >
                                        <CustomCalendar
                                            showDoubleView={true}
                                            selectRange={true}
                                            value={customValue}
                                            onChange={customOnChange}
                                        />
                                        <div className="flex justify-end">
                                            <Button
                                                btnText={"Close"}
                                                btnClassname={"!w-fit !py-2"}
                                                onClick={() => setShowCustomDate(false)}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div> */}
                        </div>

                        <Button
                            btnText={"Refresh"}
                            btnClassname={
                                "!w-fit !rounded-[6px] !shadow-buttonShadow2 !border !border-slate-100 !bg-[#FBFDFF] !py-[9px] !text-g-55"
                            }
                            onClick={() => {
                                setRefresh(true);
                            }}
                            btnType="textFirst"
                            icon={<i className="ri-refresh-line text-g-55"></i>}
                        />
                    </div>
                </div>

                <div className={`px-6 ${type === "chat" ? "block" : "xl:flex"} items-center`}>
                    <div className={`${type === "chat" ? "w-full mb-6" : "xl:w-[55%]"}`}>
                        {!Boolean(data?.totalSale) &&
                        !Boolean(data?.forecastTotalSale) &&
                        !gwmLoading ? (
                            <div className="w-[100%] h-[300px] flex items-center justify-center">
                                <div className="flex items-center  gap-[3px] flex-col w-[353px] justify-center">
                                    <img
                                        src="https://storage.googleapis.com/heysynth_files/synth_webapp/icon_w8c4mn.png"
                                        alt=""
                                        className=""
                                    />
                                    <p className="text-g-75 font-rocGroteskBold text-base ">
                                        {" "}
                                        No sales data available{" "}
                                    </p>
                                </div>
                            </div>
                        ) : gwmLoading ? (
                            <div className="flex justify-center">
                                <div className="mt-10 w-[80%]">
                                    <div className="flex gap-5">
                                        <div className="flex flex-col justify-between gap-[17px]">
                                            <span className="block text-[9px] text-slate-500">
                                                70k
                                            </span>
                                            <span className="block text-[9px] text-slate-500">
                                                60k
                                            </span>
                                            <span className="block text-[9px] text-slate-500">
                                                50k
                                            </span>
                                            <span className="block text-[9px] text-slate-500">
                                                40k
                                            </span>
                                            <span className="block text-[9px] text-slate-500">
                                                30k
                                            </span>
                                            <span className="block text-[9px] text-slate-500">
                                                20k
                                            </span>
                                            <span className="block text-[9px] text-slate-500">
                                                10k
                                            </span>
                                        </div>

                                        <div className="flex gap-2 justify-between w-full">
                                            {Array.from({ length: 6 }).map((_, idx) => (
                                                <div key={idx} className="flex gap-1 items-end">
                                                    <Skeleton
                                                        height={60}
                                                        width={10}
                                                        highlightColor="#E2E8F0"
                                                        borderRadius={1}
                                                        baseColor="#F1F5F9"
                                                        className="custom-skeleton"
                                                    />
                                                    <Skeleton
                                                        height={90}
                                                        width={10}
                                                        highlightColor="#E2E8F0"
                                                        borderRadius={1}
                                                        baseColor="#F1F5F9"
                                                        className="custom-skeleton"
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2 justify-between pt-[15px] border-t border-slate-200 ml-10">
                                        <span className="block text-[9px] text-slate-500">Jan</span>
                                        <span className="block text-[9px] text-slate-500">Feb</span>
                                        <span className="block text-[9px] text-slate-500">Mar</span>
                                        <span className="block text-[9px] text-slate-500">Apr</span>
                                        <span className="block text-[9px] text-slate-500">May</span>
                                        <span className="block text-[9px] text-slate-500">Jun</span>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <ReactECharts option={multiBarChartOption} />
                        )}
                    </div>
                    <div
                        className={`relative ${
                            type === "chat" ? "w-full" : "xl:w-[45%] xl:left-[-20px]"
                        }`}
                    >
                        {fetchingSalesForecast ? (
                            <div className="max-h-[300px] overflow-y-hidden">
                                <CustomTableLoader
                                    tableHeader={tableComponentHeader?.slice(0, 6)}
                                />
                            </div>
                        ) : (
                            <div className="shadow-sm">
                                <CustomTable
                                    tableBody={tableComponentBody}
                                    tableHeader={tableComponentHeader}
                                    isAllSelectable={false}
                                    isCellSelectable={false}
                                    isCellBordered={true}
                                    stickyHeader={true}
                                    scrollWithin={false}
                                    headerItemClass="font-rocGroteskMedium !border-slate-200  !text-slate-700 !py-[6px] !text-xs"
                                    isScrollable={false}
                                    bodyItemClass="!border-slate-200 hover:!bg-transparent !cursor-default"
                                    headerContainerClass="!bg-slate-50 !border-slate-200"
                                    tableClass="!border-none !border-[0px]"
                                />
                            </div>
                        )}
                    </div>
                </div>

                {lastSyncedDate && (
                    <div
                        className={`ml-6 flex !w-fit items-center space-x-1 border  rounded-[3px] px-2  text-sm font-rocGroteskMedium ${
                            lastSyncedForcast === 0
                                ? "bg-[#DCFCE7] border-[#16A34A] text-[#16A34A]"
                                : lastSyncedForcast === 1
                                ? "bg-[#FEF3C7] border-[#D97706] text-[#D97706]"
                                : lastSyncedForcast === 2
                                ? "bg-[#FFEDD5] border-[#EA580C] text-[#EA580C]"
                                : "bg-[#FEE2E2] border-[#DC2626] text-[#DC2626]"
                        }`}
                    >
                        <i
                            className={`ri-refresh-line text-sm  ${
                                lastSyncedForcast === 0
                                    ? "text-[#16A34A]"
                                    : lastSyncedForcast === 1
                                    ? " text-[#D97706]"
                                    : lastSyncedForcast === 2
                                    ? " text-[#EA580C]"
                                    : "text-[#DC2626]"
                            }`}
                        ></i>{" "}
                        <p className="text-sm">Last Synced: {moment(lastSyncedDate)?.fromNow()}</p>
                        <SynthTooltip
                            content={
                                "The most recent date this data was synced from your source systems."
                            }
                            divId={"last-synced-forecast"}
                            icon={
                                <i
                                    className={`ri-information-line ${
                                        lastSyncedForcast === 0
                                            ? "text-[#16A34A]"
                                            : lastSyncedForcast === 1
                                            ? " text-[#D97706]"
                                            : lastSyncedForcast === 2
                                            ? " text-[#EA580C]"
                                            : "text-[#DC2626]"
                                    } text-sm`}
                                ></i>
                            }
                            styling={{ width: "250px" }}
                        />
                    </div>
                )}
            </div>

            <div className="pt-2">
                <div className="flex justify-between items-center mb-4">
                    <h4 className="text-xl font-rocGroteskMedium">Sales orders</h4>

                    {/* <Dropdown
                        handleChange={(name, value) => {
                        }}
                        dropdown={
                            <div className="flex items-center whitespace-nowrap h-[40px] py-2 px-4 space-x-1 rounded-[5px] border border-slate-200">
                                <i className="ri-map-pin-line text-sm text-g-75 "></i>
                                <span className={`text-g-75 font-rocGroteskMedium text-[13px]`}>
                                    {warehouseValue?.name || "Warehouses"}
                                </span>
                                {warehouseValue?.name && (
                                    <i
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setUpdatedPage(1);
                                            handleClearFilter();
                                        }}
                                        className="ri-close-circle-line text-slate-500"
                                    ></i>
                                )}
                                <i className="ri-arrow-down-s-line text-g-75"></i>
                            </div>
                        }
                        search={
                            <div>
                                <TextInput
                                    value={search}
                                    name={"search"}
                                    type={"text"}
                                    required={false}
                                    onChange={handleSearch}
                                    onInput={handleDebouncedChange}
                                    inputPlaceholder="Search warehouse"
                                    containerClassname=""
                                    // inputContainerClassname={"!border-none !rounded-none "}
                                    inputClassName="w-full"
                                />
                            </div>
                        }
                        dropdownContainerClasses={`shadow-cardShadow-4 border border-slate-100 left-[-172px] rounded !max-h-fit`}
                        dropdownOptions={[
                            {
                                customChild: (
                                    <div>
                                        <div className="max-h-[280px]">
                                            {allWarehouses?.map((warehouse) => {
                                                return (
                                                    <div
                                                        key={warehouse?._id}
                                                        onClick={() => {
                                                            setUpdatedPage(1);
                                                            handleFilterChange(
                                                                warehouse?.warehouseName,
                                                                warehouse?._id
                                                            );
                                                        }}
                                                    >
                                                        <div
                                                            className={`flex items-center space-x-3 px-3 py-2 hover:bg-slate-100 cursor-pointer ${
                                                                warehouseValue?.value ===
                                                                    warehouse?._id && "bg-slate-100"
                                                            }`}
                                                        >
                                                            <i className="ri-map-pin-line text-base text-g-75 "></i>
                                                            <div>
                                                                <span
                                                                    className={`block text-g-75 text-sm font-rocGroteskMedium ${
                                                                        warehouse?.address
                                                                            ?.address && "mb-[2px]"
                                                                    }`}
                                                                >
                                                                    {truncate(
                                                                        warehouse?.warehouseName,
                                                                        { length: 40 }
                                                                    )}
                                                                </span>
                                                                <span className="block text-g-75 text-sm font-rocGroteskMedium ">
                                                                    {truncate(
                                                                        warehouse?.address?.address,
                                                                        { length: 40 }
                                                                    )}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ),
                            },
                        ]}
                    /> */}

                    <MultiChannelSelect
                        salesChannel={salesChannel}
                        value={salesOrderChannelFilterValue}
                        onChange={(value) => handleSalesOrderChannelChange(value)}
                        isLoading={salesChannelLoading}
                    />
                </div>

                <div>
                    {salesOrderData.length > 0 ? (
                        <div>
                            <CustomTable
                                tableBody={tableBody}
                                tableHeader={tableHeader}
                                isScrollable={false}
                                isCellBordered={false}
                                isCellSelectable={false}
                                isAllSelectable={false}
                                headerContainerClass="!bg-slate-50"
                                headerItemClass="font-rocGroteskMedium "
                                tableClass="mb-6"
                            />

                            {isFetching && isLoadingMore && (
                                <div className="flex justify-center my-4">
                                    <Loader color="gm-25" size={4} />
                                </div>
                            )}
                            {pagination?.currentPage === pagination?.noOfPages && (
                                <div className="flex my-4 justify-center text-slate-500 text-sm font-rocGroteskMedium">
                                    End of list
                                </div>
                            )}
                        </div>
                    ) : (
                        <div>
                            <div className="flex justify-center items-center h-[300px]">
                                <span className="text-g-75 font-rocGroteskMedium text-lg">
                                    No sales data available
                                </span>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Sales;
